import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityProps, ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { Box, useTheme } from '@mui/material';
import {
    LogActionDossierLogActionDtoJsonldLogRead,
    LogActionDossierLogActionDtoJsonldLogReadExpanded,
    type FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import useActionLogger from '@europrocurement/l2d-modules/hooks/useActionLogger';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import {
    REALM_INTERN,
    UseKeycloakCheckRole,
    UseKeycloakService,
} from '@europrocurement/l2d-keycloak';
import { UserJsonld } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import IAMenu from '../../IaServices/components/IAMenu/IAMenu';
import {
    SET_CURRENT_DOSSIER_NOTES_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
} from '../providers/CurrentDossierProvider';
import { useTiersOfferContext } from '../../Offres';
import { useUser } from '../../Utilisateur';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

type UseActivitiesHookProps = {
    actionOnSuccess?: () => void;
    defaultMemoClient?: boolean;
    useMemoClient?: boolean;
    requestLogActionLimited?: boolean;
    loadNotes?: boolean;
    loadLogs?: boolean;
};
const useActivitiesHook = ({
    actionOnSuccess = undefined,
    useMemoClient = true,
    defaultMemoClient = false,
    requestLogActionLimited = true,
    loadNotes = false,
    loadLogs = false,
}: UseActivitiesHookProps) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isSubmittingRef = useRef(false);

    const setSubmitting = useCallback((value: boolean) => {
        isSubmittingRef.current = value;
        setIsSubmitting(value);
    }, []);
    const [activities, setActivities] = useState<ActivityProps[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [withTechnicalLogs, setWithTechnicalLogs] = useState(false);

    const { modalActions } = useModal();
    const { dossier } = useCurrentDossierCtx();
    const dispatchCurrentDossier = useCurrentDossierCtxDispatch();

    const { dossierApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const { getLogActionDossier } = useActionLogger();
    const { offerDataLoaded, getTiersCurrentOfferWithDefault } = useTiersOfferContext();
    const [utilisateurPremium, setUtilisateurPremium] = useState<boolean>(false);
    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const roleChecker = UseKeycloakCheckRole();
    const isInterne = roleChecker(REALM_INTERN);
    const { cache, fetchUser } = useUser();

    useEffect(() => {
        if (!offerDataLoaded || !dossier) {
            return;
        }
        (async () => {
            const offer = await getTiersCurrentOfferWithDefault(dossier.prescripteurId as number);
            setUtilisateurPremium(offer?.type === 'FULL');
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.prescripteurId, offerDataLoaded]);

    const renderIAMenu = useCallback(
        (content: string, setContent: (value: React.SetStateAction<string>) => void) => (
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                <IAMenu
                    sxProps={{
                        position: 'absolute',
                        right: '5px',
                        top: '18px',
                    }}
                >
                    <IaButton
                        getParams={() => ({
                            textContent: content,
                        })}
                        onSuccess={(data) => {
                            const correctedText = data.answer;
                            if (correctedText) {
                                setContent(correctedText);
                                enqueueSnackbar('Correction effectuée');
                            }
                        }}
                    />
                    <IaButton
                        applicationName="L2D_Markdown"
                        label="Mettre en page avec Markdown"
                        getParams={() => ({
                            textContent: content,
                        })}
                        onSuccess={(data) => {
                            const correctedText = data.answer;
                            if (correctedText) {
                                setContent(correctedText);
                                enqueueSnackbar('Mise en page effectuée');
                            }
                        }}
                    />
                </IAMenu>
            </Box>
        ),
        [enqueueSnackbar],
    );

    const handleAddNoteDirectly = useCallback(
        async (
            content: string,
            highlighted = false,
            memoClient = false,
            dossierOverride?: DossierOpenSearch,
        ) => {
            const targetId = dossierOverride?.id ?? dossier?.id;
            if (!targetId || isSubmittingRef.current) return;

            try {
                setSubmitting(true);
                const newNote = await dossierApi.apiDossiersIdNotesPost({
                    id: targetId.toString(),
                    apiDossiersIdNotesPostRequest: { content, highlighted, memoClient },
                });

                if (
                    !dossierOverride &&
                    typeof dispatchCurrentDossier !== 'undefined' &&
                    dispatchCurrentDossier !== null
                ) {
                    dispatchCurrentDossier({
                        type: SET_CURRENT_DOSSIER_NOTES_CTX,
                        payload: [...(dossier?.notes || []), newNote.data],
                    });
                }
                if (typeof actionOnSuccess !== 'undefined') {
                    actionOnSuccess();
                }
            } catch (error: unknown) {
                console.error('Erreur lors de la création de la note:', error);
                enqueueSnackbar('Erreur lors de la création de la note', { variant: 'error' });
            } finally {
                setSubmitting(false);
            }
        },
        [
            actionOnSuccess,
            dispatchCurrentDossier,
            dossier?.id,
            dossier?.notes,
            dossierApi,
            enqueueSnackbar,
            setSubmitting,
        ],
    );

    /**
     * Gère l'ajout d'une nouvelle note
     */
    const handleAddNote = useCallback(() => {
        modalActions.call(
            <ModalCreateNote
                renderIAMenu={renderIAMenu}
                defaultMemoClient={defaultMemoClient}
                onConfirm={async (content: string, highlighted: boolean, memoClient: boolean) => {
                    if (!dossier?.id || isSubmittingRef.current) return;

                    try {
                        setSubmitting(true);
                        const newNote = await dossierApi.apiDossiersIdNotesPost({
                            id: dossier?.id.toString(),
                            apiDossiersIdNotesPostRequest: {
                                content,
                                highlighted,
                                memoClient,
                            },
                        });

                        if (
                            typeof dispatchCurrentDossier !== 'undefined' &&
                            dispatchCurrentDossier !== null
                        ) {
                            dispatchCurrentDossier({
                                type: SET_CURRENT_DOSSIER_NOTES_CTX,
                                payload: [...(dossier.notes || []), newNote.data],
                            });
                        }

                        enqueueSnackbar(`Note créée avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error: unknown) {
                        console.error('Erreur lors de la création de la note:', error);
                        enqueueSnackbar(`Erreur lors de la création de la note`, {
                            variant: 'error',
                        });
                    } finally {
                        setSubmitting(false);
                    }
                }}
                useMemoClient={useMemoClient}
            />,
        );
    }, [
        actionOnSuccess,
        defaultMemoClient,
        dispatchCurrentDossier,
        dossier?.id,
        dossier?.notes,
        dossierApi,
        enqueueSnackbar,
        modalActions,
        renderIAMenu,
        setSubmitting,
        useMemoClient,
    ]);

    /**
     * Gère l'édition d'une note existante
     */
    const handleEditNote = (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => {
        modalActions.call(
            <ModalEditNote
                initialContent={content}
                initialHighlighted={highlighted}
                initialMemoClient={memoClient}
                useMemoClient
                isSaving={isSubmitting}
                renderIAMenu={renderIAMenu}
                onConfirm={async (
                    newContent: string,
                    newHighlighted: boolean,
                    newMemoClient: boolean,
                ) => {
                    if (!dossier?.id || isSubmittingRef.current) return;

                    try {
                        setSubmitting(true);

                        const editedNote = await dossierApi.apiDossiersIdNotesNoteIdPut({
                            id: dossier?.id,
                            noteId: +noteId.replace('note-', ''),
                            apiDossiersIdNotesPostRequest: {
                                content: newContent,
                                highlighted: newHighlighted,
                                memoClient: newMemoClient,
                            },
                        });

                        if (
                            typeof dispatchCurrentDossier !== 'undefined' &&
                            dispatchCurrentDossier !== null
                        ) {
                            dispatchCurrentDossier({
                                type: SET_CURRENT_DOSSIER_NOTES_CTX,
                                payload: (dossier.notes || []).map((note) => {
                                    if (note.id === editedNote.data.id) {
                                        return editedNote.data;
                                    }
                                    return note;
                                }),
                            });
                        }
                        enqueueSnackbar(`Note mise à jour avec succès`);
                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la modification de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(`Vous n'avez pas les droits pour modifier cette note`, {
                                variant: 'error',
                            });
                        } else {
                            enqueueSnackbar(`Erreur lors de la modification de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    /**
     * Gère la suppression d'une note
     */
    const handleDeleteNote = (noteIdStr: string) => {
        modalActions.call(
            <ConfirmationModal
                messages={NOTE_DELETE_MESSAGES}
                color={palette.error}
                disabled={isSubmitting}
                actionOnValidation={async () => {
                    if (!dossier?.id || isSubmittingRef.current) return;

                    try {
                        setSubmitting(true);
                        const noteId = +noteIdStr.replace('note-', '');
                        await dossierApi.apiDossiersIdNotesNoteIdDelete({
                            id: dossier?.id,
                            noteId,
                        });

                        if (
                            typeof dispatchCurrentDossier !== 'undefined' &&
                            dispatchCurrentDossier !== null
                        ) {
                            dispatchCurrentDossier({
                                type: SET_CURRENT_DOSSIER_NOTES_CTX,
                                payload: (dossier.notes || []).filter((note) => note.id !== noteId),
                            });
                        }

                        enqueueSnackbar(`Note supprimée avec succès`);

                        if (typeof actionOnSuccess !== 'undefined') {
                            actionOnSuccess();
                        } else {
                            modalActions.close();
                        }
                    } catch (error) {
                        console.error('Erreur lors de la suppression de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            (error &&
                                typeof error === 'object' &&
                                'response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(
                                `Vous n'avez pas les droits pour supprimer cette note`,
                                {
                                    variant: 'error',
                                },
                            );
                        } else {
                            enqueueSnackbar(`Erreur lors de la suppression de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
                actionOnCancellation={() => modalActions.close()}
            />,
        );
    };

    /**
     * Formate le nom et prénom d'un utilisateur selon les conventions d'affichage
     */
    const formatUserName = (
        prenom?: string,
        nom?: string,
    ): { firstName: string; lastName: string } => {
        if (!prenom || !nom) {
            return { firstName: '', lastName: '' };
        }

        return {
            firstName: prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase(),
            lastName: nom.toUpperCase(),
        };
    };

    /**
     * Résout les informations utilisateur à partir d'un email avec mise en cache
     */
    const resolveUserInfo = useCallback(
        async (userKeycloak: string): Promise<UserJsonld | null> => {
            if (cache[userKeycloak] !== undefined) {
                return cache[userKeycloak];
            }
            try {
                const user = await fetchUser(userKeycloak);
                return user;
            } catch {
                return null;
            }
        },
        [fetchUser, cache],
    );

    const createNoteActivity = useCallback(
        async (
            note: FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
        ): Promise<ActivityProps> => {
            const createdByUser = note.createdBy ? await resolveUserInfo(note.createdBy) : null;
            const updatedByUser = note.updatedBy ? await resolveUserInfo(note.updatedBy) : null;

            const createdByFormatted = createdByUser
                ? formatUserName(createdByUser?.firstName ?? '', createdByUser?.lastName ?? '')
                : { firstName: 'Anonyme', lastName: '' };

            const updatedByFormatted = updatedByUser
                ? formatUserName(updatedByUser.firstName ?? '', updatedByUser.lastName ?? '')
                : { firstName: note.updatedBy ? 'Anonyme' : '', lastName: '' };

            const userId = createdByUser?.username || note.createdBy || 'unknown';

            return {
                id: `note-${note.id}`,
                type: 'note',
                noteType: note.highlighted ? 'important' : 'standard',
                content: note.content || '',
                createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                userId,
                userFirstName: createdByFormatted.firstName,
                userLastName: createdByFormatted.lastName,
                updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                updatedBy: note.updatedBy || undefined,
                updatedByFirstName: updatedByFormatted.firstName,
                updatedByLastName: updatedByFormatted.lastName,
                memoClient: note.memoClient || false,
            };
        },
        [resolveUserInfo],
    );

    const createLogActivity = useCallback(
        async (
            item:
                | LogActionDossierLogActionDtoJsonldLogRead
                | LogActionDossierLogActionDtoJsonldLogReadExpanded,
        ): Promise<ActivityProps> => {
            const uid = item.idUserKeycloak ?? '';
            let userFromCache: UserJsonld | { firstName: string; lastName: string } | null;

            // si interne ou premium ou connecté oin affiche le nom sur le log
            if (uid !== '' && (uid === userIdentifier || utilisateurPremium || isInterne)) {
                userFromCache = await resolveUserInfo(uid);
            } else {
                userFromCache = { firstName: 'Anonyme', lastName: '' };
            }

            const logData = 'data' in item && item.data ? item.data : undefined;
            return {
                type: 'action',
                userFirstName: uid ? (userFromCache?.firstName ?? 'Anonyme') : '',
                userLastName: uid ? (userFromCache?.lastName ?? '') : '',
                actionType: item.actionType?.logCategories?.[0]?.category ?? '',
                createdAt: item.date ? new Date(item.date as string) : new Date(),
                id: String(item['@id'] ?? 0),
                objectType: item.actionType?.actionTypeLibelle ?? '',
                description: item.logMessage ?? '',
                memoClient: false,
                logData,
            };
        },
        [isInterne, utilisateurPremium, userIdentifier, resolveUserInfo],
    );

    /**
     * Ajoute les activités de logs et de notes
     */
    const addActivities = useCallback(
        async (
            dataLogs:
                | LogActionDossierLogActionDtoJsonldLogRead[]
                | LogActionDossierLogActionDtoJsonldLogReadExpanded[],
            includeNotes: boolean,
        ) => {
            const logActivities = await Promise.all(dataLogs.map(createLogActivity));
            const noteActivities = includeNotes
                ? await Promise.all((dossier?.notes || []).map(createNoteActivity))
                : [];

            const allActivities = [...logActivities, ...noteActivities].sort(
                (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
            );

            return allActivities;
        },
        [createLogActivity, createNoteActivity, dossier?.notes],
    );

    /**
     * Charge les logs et/ou les notes selon les flags loadLogs / loadNotes
     */
    const loadAllActivities = useCallback(async () => {
        if (!dossier?.id) return;

        let logsData:
            | LogActionDossierLogActionDtoJsonldLogRead[]
            | LogActionDossierLogActionDtoJsonldLogReadExpanded[] = [];
        if (loadLogs) {
            try {
                logsData = await getLogActionDossier(
                    dossier.id,
                    requestLogActionLimited,
                    withTechnicalLogs,
                );
            } catch (error) {
                console.error('Erreur lors du chargement des logs:', error);
            }
        }

        try {
            const allActivities = await addActivities(logsData, loadNotes);
            setActivities(allActivities);
        } catch (error) {
            console.error('Erreur lors de la construction des activités:', error);
        } finally {
            setActivitiesLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dossier?.id,
        requestLogActionLimited,
        withTechnicalLogs,
        addActivities,
        loadLogs,
        loadNotes,
    ]);

    /**
     * Recharger avec les logs techniques si demandé
     */
    const loadActivitiesTechniques = (value: boolean) => {
        setWithTechnicalLogs(value);
    };

    useEffect(() => {
        if (!loadLogs && !loadNotes) {
            return;
        }
        loadAllActivities();
    }, [loadAllActivities, loadLogs, loadNotes, dossier?.notes]);

    return {
        activities,
        activitiesLoading,
        handleAddNote,
        handleAddNoteDirectly,
        handleEditNote,
        handleDeleteNote,
        loadActivitiesTechniques,
    };
};

export default useActivitiesHook;
